import React, { useEffect, useState, useCallback } from 'react';
import { FaLessThan, FaGreaterThan, FaArrowRight } from "react-icons/fa";
import Aos from 'aos';
import 'aos/dist/aos.css';
import cloudBenefits from '../assets/data/cloudBenefits';
import cloudService from '../assets/data/cloudService';
import cloudBannerImg from "../assets/cloud_banner.jpg"; 
import benefitsPromoImg from "../assets/benefits_prom.jpg"; 

const CloudComputing = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [autoSlideInterval, setAutoSlideInterval] = useState(null);

  const totalItems = cloudBenefits.length;

  const nextBenefit = useCallback(() => {
    setStartIndex((prevIndex) => (prevIndex + 1) % totalItems);
  }, [totalItems]);

  const previousBenefit = useCallback(() => {
    setStartIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  }, [totalItems]);

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });

    const interval = setInterval(() => {
      nextBenefit();
    }, 4000);

    setAutoSlideInterval(interval);
    return () => clearInterval(interval);
  }, [nextBenefit]); // Added nextBenefit as dependency

  const handleManualNavigation = () => {
    if (autoSlideInterval) {
      clearInterval(autoSlideInterval);
    }
    const interval = setInterval(() => {
      nextBenefit();
    }, 4000);
    setAutoSlideInterval(interval);
  };

  const getVisibleBenefits = () => {
    const items = [];
    for (let i = 0; i < 4; i++) {
      items.push(cloudBenefits[(startIndex + i) % totalItems]);
    }
    return items;
  };

  const visibleBenefits = getVisibleBenefits();

  return (
    <div className='w-full min-h-screen bg-white px-6 py-12 md:px-16 lg:px-24 xl:px-32 flex flex-col gap-16 md:gap-24 font-sans'>
      
      {/* ================= TOP CONTAINER: CLOUD COMPUTING SERVICE ================= */}
      <section 
        className='w-full bg-bgColorTwo rounded-[32px] p-6 md:p-10 lg:p-14 flex flex-col gap-8 shadow-sm max-w-[1200px] mx-auto'
        data-aos="fade-up"
      >
        <div className='w-full rounded-2xl overflow-hidden h-[180px] sm:h-[240px] md:h-[280px] lg:h-[320px]'>
          <img 
            src={cloudBannerImg} 
            alt="Cloud Computing Graphics" 
            className='w-full h-full object-cover object-center select-none pointer-events-none'
          />
        </div>

        <div className='flex flex-col gap-4 max-w-[1050px]'>
          <h2 className='text-white text-3xl md:text-[40px] font-bold tracking-tight leading-tight'>
            {cloudService[0]?.title || "Cloud Computing Service"}
          </h2>
          <p className='text-cloudText text-base md:text-lg leading-relaxed font-normal'>
            {cloudService[0]?.description}
          </p>
        </div>
      </section>

      {/* ================= BOTTOM CONTAINER: BENEFITS MATRIX SECTION ================= */}
      <section className='w-full max-w-[1200px] mx-auto flex flex-col gap-8' data-aos="fade-up">
        
        {/* Dynamic Title Row + Carousel Navigation Arrows */}
        <div className='flex items-center justify-between pl-2 pr-2'>
          <h2 className='text-black text-4xl md:text-[52px] font-bold tracking-tight'>
            Benefits
          </h2>
          
          <div className='flex gap-3'>
            <button 
              className='bg-[#F1F5F9] hover:bg-[#E2E8F0] h-11 w-11 rounded-full flex justify-center items-center text-[#4A5568] transition shadow-sm' 
              onClick={() => { handleManualNavigation(); previousBenefit(); }}
            >
              <FaLessThan className='text-sm' />
            </button>
            <button 
              className='bg-[#F1F5F9] hover:bg-[#E2E8F0] h-11 w-11 rounded-full flex justify-center items-center text-[#4A5568] transition shadow-sm' 
              onClick={() => { handleManualNavigation(); nextBenefit(); }}
            >
              <FaGreaterThan className='text-sm' />
            </button>
          </div>
        </div>

        {/* Master Structural Grid Frame */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch'>
          
          {/* 1. Left/Center Columns: 2x2 Benefits Grid */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 lg:col-span-2 order-2 lg:order-1 content-start'>
            {visibleBenefits.map((benefit, index) => (
              <div 
                key={`${benefit?.title || index}-${index}`}
                className='bg-white border-2 border-[#E2E8F0] rounded-[24px] p-6 lg:p-8 flex flex-col gap-4 justify-start shadow-sm hover:border-[#BFDBFE] transition-all duration-300 min-h-[220px] lg:min-h-[240px]'
              >
                {benefit?.img && (
                  <img 
                    src={benefit.img} 
                    alt="Benefit Indicator Icon" 
                    className='h-10 w-10 object-contain self-start'
                  />
                )}
                <h4 className='text-[#060080] text-xl lg:text-2xl font-bold tracking-tight'>
                  {benefit?.title}
                </h4>
                <p className='text-[#4A5568] text-sm lg:text-base leading-relaxed font-normal'>
                  {benefit?.description}
                </p>
              </div>
            ))}
          </div>

          {/* 2. Right Column: Promo Container
            Uses a solid height container on mobile (h-[420px]) so the split-percentages maintain 
            perfect structural visibility without breaking your clean absolute image crop.
          */}
          <div className='rounded-[28px] overflow-hidden flex flex-col items-stretch lg:col-span-1 order-1 lg:order-2 shadow-sm h-[420px] lg:h-full lg:max-h-full bg-white border border-[#E2E8F0]'>
            
            {/* Top Text Box Container - Scaled to take exactly 35% height layout share */}
            <div className='bg-[#A7E70C] h-[35%] p-6 md:p-8 flex flex-col justify-center items-start gap-4 w-full rounded-t-[28px] shrink-0'>
              <h3 className='text-[#022447] text-xl md:text-2xl lg:text-[24px] font-bold leading-tight tracking-tight'>
                Benefits of<br />Andurar Cloud Services
              </h3>
              <button className='bg-black text-white px-4 py-2 lg:px-5 lg:py-2.5 rounded-full flex items-center justify-center gap-2 text-xs lg:text-sm font-semibold shadow hover:bg-opacity-80 transition transform active:scale-95 whitespace-nowrap'>
                Explore More
                <FaArrowRight className='text-[10px]' />
              </button>
            </div>

            {/* Bottom Image Box - Exactly 65% height split scale. */}
            <div className='w-full h-[65%] relative rounded-b-[28px] overflow-hidden bg-slate-50'>
              <img 
                src={benefitsPromoImg} 
                alt="Andurar Enterprise Representative Graphic Banner" 
                className='absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none'
              />
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default CloudComputing;